import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronDown, Facebook, Instagram, Menu, X, Youtube } from "lucide-react";
import { API, useAuth } from "@/App";
import { RichText } from "@/components/RichText";
import { SmartLink } from "@/website/sections/Basic";

const SiteCtx = createContext(null);
export const useSite = () => useContext(SiteCtx);

export const SiteProvider = ({ children }) => {
  const [site, setSite] = useState(null);
  useEffect(() => { axios.get(`${API}/website/site`).then((r) => setSite(r.data)).catch(() => setSite({ nav: [], footer: {}, theme: {} })); }, []);
  return <SiteCtx.Provider value={site}>{children}</SiteCtx.Provider>;
};

const DASH = { admin: "/admin", teacher: "/teacher", student: "/student" };

const NavItem = ({ item, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const kids = item.children || [];
  if (!kids.length) return <SmartLink href={item.href} className="px-3 py-2 rounded-lg hover:bg-slate-100 font-medium text-slate-700" testId={`site-nav-${item.label}`}>{item.label}</SmartLink>;
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="px-3 py-2 rounded-lg hover:bg-slate-100 font-medium text-slate-700 inline-flex items-center gap-1" onClick={() => setOpen(!open)} data-testid={`site-nav-${item.label}`}>{item.label}<ChevronDown className="w-4 h-4" /></button>
      {open && (
        <div className="lg:absolute left-0 top-full lg:pt-2 z-50">
          <div className="lg:w-64 rounded-xl bg-white lg:shadow-xl lg:border border-slate-100 p-2">
            {item.href && <SmartLink href={item.href} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-sm font-semibold">{item.label} overview</SmartLink>}
            {kids.map((k, i) => <div key={i} onClick={onNavigate}><SmartLink href={k.href} className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-sm text-slate-700">{k.label}</SmartLink></div>)}
          </div>
        </div>
      )}
    </div>
  );
};

const Header = ({ site }) => {
  const { user } = useAuth();
  const [mobile, setMobile] = useState(false);
  const cta = site.cta || {};
  const nav = (
    <>
      {(site.nav || []).map((n, i) => <NavItem key={i} item={n} onNavigate={() => setMobile(false)} />)}
    </>
  );
  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/70" data-testid="site-header">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="site-logo"><img src="/logo.png" alt="TheMatheMind" className="h-9 rounded-lg" /><span className="hidden sm:inline font-extrabold font-['Manrope'] text-lg text-slate-900 whitespace-nowrap">TheMatheMind <span className="text-amber-600">360</span></span></Link>
        <nav className="hidden lg:flex items-center gap-1 ml-4 flex-1">{nav}</nav>
        <div className="ml-auto flex items-center gap-2">
          <Link to={user ? DASH[user.role] || "/" : "/login"} className="hidden sm:inline-flex h-10 items-center px-4 rounded-full font-semibold text-slate-700 hover:bg-slate-100" data-testid="site-login-btn">{user ? "My Dashboard" : "Student Login"}</Link>
          {cta.label && <SmartLink href={cta.href} className="inline-flex h-10 items-center px-4 sm:px-5 rounded-full font-semibold bg-[var(--site-accent)] text-slate-900 hover:brightness-105 whitespace-nowrap" testId="site-cta-btn">{cta.label}</SmartLink>}
          <button className="lg:hidden p-2" onClick={() => setMobile(!mobile)} aria-label="Menu" data-testid="site-mobile-menu-btn">{mobile ? <X /> : <Menu />}</button>
        </div>
      </div>
      {mobile && (
        <nav className="lg:hidden border-t border-slate-100 bg-white px-5 py-3 flex flex-col" data-testid="site-mobile-menu">
          {nav}
          <Link to={user ? DASH[user.role] || "/" : "/login"} className="px-3 py-2 font-semibold text-[var(--site-primary)]">{user ? "My Dashboard" : "Student Login"}</Link>
        </nav>
      )}
    </header>
  );
};

const Footer = ({ site }) => {
  const f = site.footer || {};
  const social = [["facebook", Facebook], ["instagram", Instagram], ["youtube", Youtube]].filter(([k]) => f.social?.[k]?.startsWith("https://"));
  return (
    <footer className="bg-[var(--site-dark)] text-slate-300" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid md:grid-cols-3 gap-8">
        <div><p className="font-extrabold font-['Manrope'] text-white text-xl">TheMatheMind</p>{f.text && <RichText html={f.text} className="mt-3 text-sm opacity-80" />}</div>
        <div className="flex flex-col gap-2 text-sm">{[...(site.nav || []), ...(f.links || [])].map((l, i) => <SmartLink key={i} href={l.href} className="hover:text-white">{l.label}</SmartLink>)}<Link to="/privacy-policy" className="hover:text-white">Privacy policy</Link></div>
        <div className="text-sm space-y-2">
          {f.contact?.email && <a href={`mailto:${f.contact.email}`} className="block hover:text-white">{f.contact.email}</a>}
          {f.contact?.phone && <a href={`tel:${f.contact.phone}`} className="block hover:text-white">{f.contact.phone}</a>}
          <div className="flex gap-3 pt-2">{social.map(([k, Icon]) => <a key={k} href={f.social[k]} target="_blank" rel="noopener noreferrer" aria-label={k} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid={`site-social-${k}`}><Icon className="w-4 h-4" /></a>)}</div>
        </div>
      </div>
      <p className="text-center text-xs text-slate-500 pb-6">© {new Date().getFullYear()} TheMatheMind. All rights reserved.</p>
    </footer>
  );
};

export const SiteLayout = ({ children }) => {
  const site = useSite();
  if (!site) return <div className="min-h-screen" />;
  const t = site.theme || {};
  return (
    <div className="min-h-screen flex flex-col bg-white" style={{ "--site-primary": t.primary || "#2563eb", "--site-accent": t.accent || "#f59e0b", "--site-dark": t.dark || "#0f172a" }} data-testid="site-layout">
      <Header site={site} />
      <main className="flex-1">{children}</main>
      <Footer site={site} />
    </div>
  );
};
